import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ManagerDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { swalTabooTheme } from "../../lib/swalTabooTheme";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLobbyGameData } from "../../functions/getLobbyGameData";
import { addManagerQuestions } from "../../functions/addManagerQuestions";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { setBackButtonUrl } from "../uiSlice";
import { initScribbleLobby } from "../../lib/scribbleLobby";
import { isFirebaseConfigured } from "../../lib/firebaseClient";
import { isAnchorModeFromSettings } from "../../lib/gameMode";
import { readManagerDraft, writeManagerDraft } from "../../lib/managerCardsSession";
import {
  appendLobbyShareFlagsToJoinUrl,
  buildExtramileJoinUrl,
  buildFallbackAppLink,
  deriveGuestFromUser,
  withQuestionAmp,
} from "./managerLobbyLinks";
import {
  TABOO_WORDS_PER_CARD,
  MAX_WORD_CHARS,
  MAX_TABOO_CHARS,
  TOPIC_CATEGORIES,
  TabooRoundCard,
  aiToCardEntry,
  clampText,
  fetchAiCards,
  isValidCardEntry,
  newCardEntry,
  readLobbyNameFromSession,
  tabooFromRow,
} from "./managerHostShared";

const ArrowPrev = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowNext = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function ManagerCards() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);

  const [draft, setDraft] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [editingCardId, setEditingCardId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);

  const maxCards = Math.max(1, Number(draft?.cardCount) || 3);
  const missingCount = Math.max(0, maxCards - cards.length);
  const needsRegen = missingCount > 0;

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  const lobbyName = draft?.lobbyName || readLobbyNameFromSession();
  const themeName = draft?.themeName || "guess_me";
  const timerMin = Number(draft?.timerMin) || 1;
  const maxPoints = Number(draft?.maxPoints) || 100;
  const topic = String(draft?.topic || "").trim();
  const topicCategory = draft?.topicCategory || "fun";

  const categoryMeta = useMemo(
    () => TOPIC_CATEGORIES.find((c) => c.id === topicCategory) || TOPIC_CATEGORIES[1],
    [topicCategory]
  );

  const effectiveTopic = useMemo(() => {
    const base = topic || "Equity and workplace culture";
    return `${base} (${categoryMeta.label} tone)`;
  }, [topic, categoryMeta.label]);

  const isAnchorMode = useMemo(() => {
    if (location?.state?.isAnchorMode) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return isAnchorModeFromSettings(cached);
    } catch {
      return false;
    }
  }, [location?.state?.isAnchorMode]);

  const saveCards = useCallback((nextCards, index) => {
    const base = readManagerDraft() || {};
    const idx = index != null ? index : cardIndex;
    const next = { ...base, cards: nextCards, cardIndex: idx };
    writeManagerDraft(next);
    setDraft(next);
  }, [cardIndex]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/manager-dashboard"));
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.classList.add("manager-static-bg");
    document.body.classList.add("manager-static-bg");
    return () => {
      document.documentElement.classList.remove("manager-static-bg");
      document.body.classList.remove("manager-static-bg");
    };
  }, []);

  useEffect(() => {
    const d = readManagerDraft();
    if (!d || !Array.isArray(d.cards)) {
      navigate("/manager-dashboard", { replace: true });
      return;
    }
    setDraft(d);
    setCards(d.cards);
    const idx = Number(d.cardIndex);
    setCardIndex(Number.isFinite(idx) && idx >= 0 ? idx : 0);
  }, [navigate]);

  useEffect(() => {
    if (!draft || hasHydrated || !tokenForApi || !uid || !lobbyName) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getLobbyGameData({
          token: tokenForApi,
          userId: uid,
          themeName,
          lobby_name: lobbyName,
        });
        if (cancelled) return;
        const rows = Array.isArray(resp?.questions) ? resp.questions : [];
        const existing = readManagerDraft()?.cards;
        if (rows.length > 0 && (!Array.isArray(existing) || existing.length === 0)) {
          const hydrated = rows.map((row) =>
            newCardEntry({
              word: clampText(String(row?.question_name || "").trim(), MAX_WORD_CHARS),
              taboo: tabooFromRow(row),
            })
          );
          setCards(hydrated);
          saveCards(hydrated, 0);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setHasHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft, hasHydrated, tokenForApi, uid, lobbyName, themeName, saveCards]);

  useEffect(() => {
    if (!draft || !cards.length) return;
    const safeIndex = Math.min(cardIndex, cards.length - 1);
    if (safeIndex !== cardIndex) setCardIndex(safeIndex);
  }, [cards.length, cardIndex, draft]);

  const currentCard = cards[cardIndex] || null;

  const canStartGame = useMemo(() => {
    if (cards.length !== maxCards) return false;
    return cards.every((c) => isValidCardEntry(c));
  }, [cards, maxCards]);

  const handleRegenerate = async () => {
    if (missingCount <= 0 || loading) return;
    setLoading(true);
    try {
      const exclude = cards.flatMap((c) => [
        String(c.word || "").trim(),
        ...(c.taboo || []).map((t) => String(t).trim()),
      ]).filter(Boolean);
      const batch = await fetchAiCards({
        topic: topic || effectiveTopic,
        categoryTone: categoryMeta.tone,
        count: missingCount,
        excludeWords: exclude,
        literalTopic: Boolean(String(topic || "").trim()),
        token: tokenForApi,
      });
      const generated = batch.map(aiToCardEntry);
      setCards((prev) => {
        const next = [...prev, ...generated].slice(0, maxCards);
        const newIndex = Math.min(prev.length, next.length - 1);
        saveCards(next, newIndex);
        setCardIndex(newIndex);
        return next;
      });
    } catch (e) {
      await Swal.fire(
        swalTabooTheme({ title: "Error", text: e?.message || "Failed to regenerate cards.", icon: "error" })
      );
    } finally {
      setLoading(false);
    }
  };

  const addBlankCard = () => {
    if (cards.length >= maxCards) return;
    const entry = newCardEntry();
    setCards((prev) => {
      const next = [...prev, entry];
      const newIndex = next.length - 1;
      saveCards(next, newIndex);
      setCardIndex(newIndex);
      return next;
    });
    setEditingCardId(entry.id);
  };

  const updateCard = (updated) => {
    setCards((prev) => {
      const next = prev.map((c) => (c.id === updated.id ? { ...updated } : c));
      saveCards(next);
      return next;
    });
  };

  const deleteCard = (id) => {
    if (editingCardId === id) setEditingCardId(null);
    setCards((prev) => {
      const next = prev.filter((c) => c.id !== id);
      const newIndex = Math.max(0, Math.min(cardIndex, next.length - 1));
      saveCards(next, newIndex);
      setCardIndex(newIndex);
      return next;
    });
  };

  const goPrev = () => setCardIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCardIndex((i) => Math.min(cards.length - 1, i + 1));

  const handleStartGame = async () => {
    if (!tokenForApi || !uid || !lobbyName) {
      Swal.fire(swalTabooTheme({ title: "Error", text: "Missing token/user/lobby.", icon: "error" }));
      return;
    }
    if (!canStartGame) {
      await Swal.fire(
        swalTabooTheme({
          title: "Not ready",
          text: `You need ${maxCards} complete cards (${cards.length}/${maxCards} now).`,
          icon: "warning",
        })
      );
      return;
    }

    const payloadQuestions = cards.map((c) => {
      const taboo = (c.taboo || []).map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS));
      return {
        question_name: clampText(String(c.word || "").trim(), MAX_WORD_CHARS),
        option_one: taboo[0] || "",
        option_two: taboo[1] || "",
        option_three: taboo[2] || "",
        hint: taboo.join(" · "),
        is_active: "true",
      };
    });

    try {
      const isGuest = deriveGuestFromUser(storedUserData);
      const gameId = String(storedUserData.gameId ?? "").trim();
      const sessionId = String(storedUserData.sessionId ?? "").trim();
      const organizationId = String(storedUserData.organizationId ?? "").trim();

      let joinLink = "";
      if (gameId && sessionId && organizationId) {
        const base = buildExtramileJoinUrl(isGuest, gameId, sessionId, organizationId);
        joinLink = withQuestionAmp(appendLobbyShareFlagsToJoinUrl(base, lobbyName));
      } else {
        joinLink = buildFallbackAppLink(lobbyName);
      }

      await updateGameSettings({
        token: tokenForApi,
        userId: uid,
        themeName,
        current_mode: "Host",
        questions_no: String(maxCards),
        time_duration: timerMin,
        points: maxPoints,
        lobby_link: joinLink || null,
        email: storedUserData?.email ?? null,
        fullName: storedUserData?.name ?? storedUserData?.fullName ?? null,
        employeeId: storedUserData?.employeeId ?? null,
        reportid: storedUserData?.reportId ?? null,
      }).catch(() => {});

      const res = await addManagerQuestions({
        token: tokenForApi,
        userId: uid,
        themename: themeName,
        lobby_name: lobbyName,
        questions: payloadQuestions,
      });

      await Swal.fire(
        swalTabooTheme({
          title: "Saved!",
          text: `Cards saved: ${res?.inserted ?? payloadQuestions.length}`,
         
        })
      );

      const cached = (() => {
        try {
          return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
        } catch {
          return {};
        }
      })();
      const timerSec = Math.max(1, timerMin) * 60;
      if (isFirebaseConfigured()) {
        await initScribbleLobby({
          lobbyName,
          hostUserId: uid,
          roundCount: maxCards,
          maxPlayers: 10,
          timerSeconds: timerSec,
          gameMode: "taboo",
        });
      }
      const gs = {
        ...cached,
        lobby_name: lobbyName,
        rounds: maxCards,
        time: timerMin,
        points: maxPoints,
        maxPoints,
      };
      sessionStorage.setItem("gameSettings", JSON.stringify(gs));
      navigate("/usergame", {
        replace: true,
        state: { lobby_name: lobbyName, backTo: "/manager-dashboard" },
      });
    } catch (e) {
      await Swal.fire(
        swalTabooTheme({ title: "Error", text: e?.message || "Failed to save cards.", icon: "error" })
      );
    }
  };

  if (!draft) {
    return (
      <div className="md-page md-page--center">
        <div className="md-confetti" aria-hidden="true" />
        <p className="md-cards-counter">Loading…</p>
      </div>
    );
  }

  return (
    <div className="md-page md-page--center md-page--cards">
      <div className="md-confetti" aria-hidden="true" />
      <div className="md-inner md-inner--center">
        <p className="md-cards-counter">
          Card {cards.length ? cardIndex + 1 : 0} of {maxCards}
          <span className="md-cards-counter-sub">
            ({cards.length} created)
          </span>
        </p>

        {needsRegen && (
          <div className="md-regen-row">
            <button
              type="button"
              className="md-generate-btn"
              onClick={() => void handleRegenerate()}
              disabled={loading}
            >
              {loading ? "Generating…" : `Regenerate ${missingCount} missing`}
            </button>
          </div>
        )}

        <div className="md-carousel">
          <button
            type="button"
            className="md-carousel-arrow"
            onClick={goPrev}
            disabled={cardIndex <= 0 || !cards.length}
            aria-label="Previous card"
          >
            <ArrowPrev />
          </button>

          <div className="md-carousel-card-wrap">
            {currentCard ? (
              <TabooRoundCard
                card={currentCard}
                roundIndex={cardIndex}
                isEditing={editingCardId === currentCard.id}
                onEdit={(id) => setEditingCardId((cur) => (cur === id ? null : id))}
                onChange={updateCard}
                onDelete={deleteCard}
                onKeep={(id) => setEditingCardId((cur) => (cur === id ? null : cur))}
              />
            ) : (
              <div className="md-taboo-card md-taboo-card--empty">
                <p>No cards yet.</p>
                <button type="button" className="md-generate-btn" onClick={() => navigate("/manager-dashboard")}>
                  Back to settings
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="md-carousel-arrow"
            onClick={goNext}
            disabled={cardIndex >= cards.length - 1 || !cards.length}
            aria-label="Next card"
          >
            <ArrowNext />
          </button>
        </div>

        <button
          type="button"
          className="md-start-btn"
          style={{ opacity: canStartGame && tokenForApi && uid && lobbyName ? 1 : 0.55 }}
          onClick={() => void handleStartGame()}
          disabled={!canStartGame || !tokenForApi || !uid || !lobbyName}
        >
          Start Game
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </button>
      </div>

      <button
        type="button"
        className="md-fab-add"
        onClick={addBlankCard}
        disabled={cards.length >= maxCards}
        title={cards.length >= maxCards ? "Card limit reached" : "Add card"}
        aria-label="Add card"
      >
        +
      </button>
    </div>
  );
}
